import type { FC } from 'react'
import { useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useTranslation } from 'react-i18next'
import { Logo } from '@/components/Logo'
import { tokens } from '@/locales/tokens'

const RECHECK_MS = 30_000

interface MaintenanceScreenProps {
  /** Fin annoncée de la fenêtre ; null quand le Worker n'en donne pas. */
  until: Date | null
}

/**
 * À la place de l'application pendant une coupure planifiée. La session est gardée : le contexte
 * d'auth réessaie seul et rouvre l'application dès que l'API répond.
 */
export const MaintenanceScreen: FC<MaintenanceScreenProps> = ({ until }) => {
  const { t, i18n } = useTranslation()
  const [, setTick] = useState(0)
  // Une fenêtre qui déborde n'annonce plus une heure déjà passée.
  const end = until && until.getTime() > Date.now() ? until : null

  // L'écran peut rester affiché au-delà de la fin, sans autre rendu : il se relit à intervalle
  // régulier, plutôt qu'à un minuteur calé sur la fin, qu'une mise en veille retarderait.
  useEffect(() => {
    if (!end) return
    const timer = setInterval(() => setTick((n) => n + 1), RECHECK_MS)
    return () => clearInterval(timer)
  }, [end])

  return (
    <Box
      component="main"
      sx={{
        alignItems: 'center',
        backgroundColor: 'background.paper',
        display: 'flex',
        justifyContent: 'center',
        minHeight: '100vh',
        p: 3,
      }}
    >
      <Stack
        spacing={2}
        sx={{ alignItems: 'center', maxWidth: 480, textAlign: 'center' }}
      >
        <Logo />
        <Typography variant="h5">
          {t(tokens.common.maintenanceTitle)}
        </Typography>
        <Typography>
          {end
            ? t(tokens.common.maintenanceUntil, {
                // Fuseau du navigateur, langue de l'interface ; l'heure suit l'usage de chaque langue.
                date: new Intl.DateTimeFormat(i18n.language, {
                  weekday: 'long',
                  day: 'numeric',
                  month: 'long',
                }).format(end),
                time: new Intl.DateTimeFormat(i18n.language, {
                  timeStyle: 'short',
                }).format(end),
              })
            : t(tokens.common.maintenanceNoEnd)}
        </Typography>
        <Typography color="text.secondary" variant="body2">
          {t(tokens.common.maintenanceSessionKept)}
        </Typography>
      </Stack>
    </Box>
  )
}
